import React, { useEffect } from 'react';
import { 
  Stethoscope, 
  Dna, 
  TrendingUp, 
  Baby, 
  AlertCircle, 
  Activity,
  ChevronRight 
} from 'lucide-react';
import Navbar from '../Navbar/Navbar';
import FooterSection from '../FooterSection/FooterSection';
import QuickAccess from '../QuickAccess/QuickAccess';
import './WhyUsSection.css';

const WhyUsSection = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="why-us-page">
      <Navbar />

      {/* HERO SECTION */}
      <section className="why-hero">
        <div className="hero-overlay"></div>
        <div className="container">
          <div className="hero-content">
            <span className="hero-badge">CERRAHİ FELSEFEMİZ</span>
            <h1>Neden Çocuk Cerrahisi:</h1>
            <p>Çocuk cerrahisi, tıp dünyasında "Çocuklar, yetişkinlerin küçültülmüş bir kopyası değildir" felsefesi üzerine kurulmuştur.</p>
          </div>
        </div>
      </section>

      {/* PHILOSOPHY SECTION */}
      <section className="philosophy-detail section-padding">
        <div className="container">
          <div className="bio-grid">
            <div className="bio-info">
              <h2>Neden Spesifik Bir Branşa İhtiyaç Duyulur?</h2>
              <p>
                Çocuk cerrahisi, tıp dünyasında <strong>"Çocuklar, yetişkinlerin küçültülmüş bir kopyası değildir"</strong> felsefesi üzerine kurulmuştur. Bu branşın varlık sebebi sadece yaş farkı değil; çocukların kendine has anatomik, fizyolojik ve duygusal ihtiyaçlarıdır.
              </p>
              <p>
                Bir yetişkin cerrahisi uzmanı yetişkin vücudunda "bozulanı" tamir etmeye odaklanırken, bir çocuk cerrahı <strong>"gelişmekte olan"</strong> veya <strong>"eksik gelişmiş"</strong> bir sistemle uğraşır.
              </p>
            </div>
            <div className="bio-visual">
              <div className="bio-image-frame">
                <img 
                  src="https://images.unsplash.com/photo-1581594693702-fbdc51b2763b?q=80&w=2000&auto=format&fit=crop" 
                  alt="Çocuk Cerrahisi Uzmanlığı" 
                  className="main-doctor-img"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3 MAIN POINTS */}
      <section className="academic-track section-padding">
        <div className="container">
          <div className="academic-grid">
            <div className="academic-card">
              <Activity className="a-icon" />
              <h4>1. Fizyolojik ve Metabolik Farklar</h4>
              <p>Çocukların sıvı-elektrolit dengesi, metabolizma hızı ve ilaçlara verdiği yanıtlar yetişkinlerden tamamen farklıdır. Bir yenidoğanda 10 ml'lik bir kan kaybı, bir yetişkindeki 1 litrelik kayba eşdeğer hayati risk taşıyabilir.</p>
            </div>
            <div className="academic-card">
              <Dna className="a-icon" />
              <h4>2. Doğuştan Gelen (Konjenital) Anomaliler</h4>
              <p>Yetişkin cerrahisi genellikle sonradan gelişen hastalıklarla (tümörler, taşlar, fıtıklar) ilgilenir. Çocuk cerrahisi ise anne karnındaki gelişim kusurlarıyla (yemek borusunun kapalı olması, diyafram fıtığı vb.) ilgilenir. Bu hastalıklar, yetişkin cerrahların eğitim müfredatında yer almayan, çok özel embriyolojik bilgi ve teknik beceri gerektirir.</p>
            </div>
            <div className="academic-card">
              <TrendingUp className="a-icon" />
              <h4>3. Büyüme ve Gelişme Perspektifi</h4>
              <p>Yapılan bir müdahalenin çocuğun 20 yıl sonraki kemik gelişimini, boy uzamasını veya hormonal dengesini nasıl etkileyeceğini bilmek gerekir. Çocuk cerrahı, dokuya "büyüyecek bir doku" gözüyle bakar.</p>
            </div>
          </div>
        </div>
      </section>

      {/* PERIODS SECTION */}
      <section className="scope-section section-padding">
        <div className="container">
          <div className="hospitals-list-container">
            <h3 className="h-list-title">Çocuk cerrahisi, modern tıpta doğum öncesinden (fetal dönem) başlayıp 18 yaşına kadar olan süreci kapsar.</h3>
            <ul className="h-list-content">
              <li><ChevronRight size={18} className="h-dot-icon" /> <strong>Antenatal Dönem:</strong> Anne karnındaki bebeğin anomalilerinin takibi ve danışmanlığı.</li>
              <li><ChevronRight size={18} className="h-dot-icon" /> <strong>Yenidoğan Dönemi:</strong> Hayatın ilk 28 günü (En kritik ve teknik cerrahilerin yapıldığı dönem).</li>
              <li><ChevronRight size={18} className="h-dot-icon" /> <strong>Bebeklik ve Çocukluk:</strong> Okul öncesi ve okul çağı.</li>
              <li><ChevronRight size={18} className="h-dot-icon" /> <strong>Adolesan (Ergenlik):</strong> 18 yaş sınırına kadar olan gelişim süreci.</li>
            </ul>
          </div>
        </div>
      </section>

      {/* PROCEDURES SECTION */}
      <section className="common-ops section-padding">
        <div className="container">
           <div className="section-title">
            <h2>En Sık Yapılan Cerrahi Müdahaleler</h2>
            <p style={{marginTop: '10px', color: '#718096'}}>Çocuk cerrahlarının günlük pratiğinde en çok karşılaştığı durumlar şunlardır:</p>
            <div className="title-line"></div>
          </div>
          <div className="academic-grid">
            <div className="academic-card">
              <Baby className="a-icon" />
              <h4>Günübirlik İşlemler:</h4>
              <p>Sünnet, kasık fıtığı, göbek fıtığı ve su fıtığı (hidrosel).</p>
            </div>
            <div className="academic-card">
              <Stethoscope className="a-icon" />
              <h4>Ürolojik Müdahaleler:</h4>
              <p>İnmemiş testis, hipospadias (peygamber sünneti) ve vezikoüreteral reflü (böbreğe idrar kaçması).</p>
            </div>
            <div className="academic-card">
              <AlertCircle className="a-icon" />
              <h4>Acil Cerrahiler:</h4>
              <p>Apandisit, invajinasyon (bağırsak düğümlenmesi) ve yabancı cisim yutma/kaçması.</p>
            </div>
            <div className="academic-card">
              <Activity className="a-icon" />
              <h4>Yenidoğan Anomalileri:</h4>
              <p>Yemek borusunun (özofagus atrezisi) veya makatın (anorektal malformasyon) kapalı olması.</p>
            </div>
            <div className="academic-card">
               <TrendingUp className="a-icon" />
              <h4>Torasik İşlemler:</h4>
              <p>Göğüs kafesi deformiteleri (kunduracı göğsü vb.) ve doğumsal akciğer kistleri.</p>
            </div>
            <div className="academic-card" style={{background: '#f1f7f7'}}>
              <h4 style={{color: '#2AB2AF'}}>Özetle:</h4>
              <p>Çocuk cerrahı, minyatür aletler ve devasa bir sabırla, bir insanın tüm geleceğini etkileyecek en hassas dokunuşları yapan hekimdir.</p>
            </div>
          </div>
        </div>
      </section>

      <FooterSection />
      <QuickAccess />
    </div>
  );
};

export default WhyUsSection;